// Customer auth — JWT bearer client backed by localStorage (persists across
// tab close, unlike the admin session which uses sessionStorage). Admin and
// customer tokens never collide.
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const TOKEN_KEY = "mb_customer_token";
const CUSTOMER_KEY = "mb_customer";

export const CUSTOMER_CHANGE_EVENT = "mb-customer-change";

const safeJSON = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const getCustomerToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
};

export const setCustomerToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* noop */
  }
};

export const getCachedCustomer = () => {
  try {
    return safeJSON(localStorage.getItem(CUSTOMER_KEY));
  } catch {
    return null;
  }
};

export const cacheCustomer = (customer) => {
  try {
    if (customer) localStorage.setItem(CUSTOMER_KEY, JSON.stringify(customer));
    else localStorage.removeItem(CUSTOMER_KEY);
  } catch {
    /* noop */
  }
  try {
    window.dispatchEvent(new Event(CUSTOMER_CHANGE_EVENT));
  } catch {
    /* noop */
  }
};

const headers = (auth = false, json = true) => {
  const h = {};
  if (json) h["Content-Type"] = "application/json";
  if (auth) {
    const t = getCustomerToken();
    if (t) h["Authorization"] = `Bearer ${t}`;
  }
  return h;
};

const handle = async (resp) => {
  if (resp.status === 204) return null;
  const text = await resp.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!resp.ok) {
    const detail =
      (data && data.detail) || (typeof data === "string" ? data : resp.statusText);
    const err = new Error(typeof detail === "string" ? detail : "Request failed");
    err.status = resp.status;
    err.detail = detail;
    throw err;
  }
  return data;
};

export const requestOtp = async (phone) => {
  const resp = await fetch(`${API}/auth/customer/request-otp`, {
    method: "POST",
    headers: headers(false, true),
    body: JSON.stringify({ phone }),
  });
  return handle(resp);
};

export const verifyOtp = async (phone, code, name) => {
  const resp = await fetch(`${API}/auth/customer/verify-otp`, {
    method: "POST",
    headers: headers(false, true),
    body: JSON.stringify({ phone, code, name }),
  });
  const data = await handle(resp);
  if (data?.token) {
    setCustomerToken(data.token);
    cacheCustomer(data.customer);
  }
  return data;
};

export const exchangeGoogleSession = async (sessionId) => {
  const resp = await fetch(`${API}/auth/customer/google-session`, {
    method: "POST",
    headers: headers(false, true),
    body: JSON.stringify({ session_id: sessionId }),
  });
  const data = await handle(resp);
  if (data?.token) {
    setCustomerToken(data.token);
    cacheCustomer(data.customer);
  }
  return data;
};

export const fetchCustomerMe = async () => {
  if (!getCustomerToken()) return null;
  try {
    const resp = await fetch(`${API}/auth/customer/me`, {
      headers: headers(true, false),
    });
    const data = await handle(resp);
    if (data) cacheCustomer(data);
    return data;
  } catch (e) {
    if (e.status === 401) logoutCustomer();
    return null;
  }
};

// Silent auto-signup used by the checkout flow — no OTP, no login screen.
// Called right after a guest COD checkout with the email + phone they typed.
export const silentSignup = async ({ email, phone, name }) => {
  try {
    const resp = await fetch(`${API}/auth/customer/silent`, {
      method: "POST",
      headers: headers(false, true),
      body: JSON.stringify({
        email: (email || "").trim(),
        phone: String(phone || "").trim(),
        name: (name || "").trim(),
      }),
    });
    const data = await handle(resp);
    if (data?.token) {
      setCustomerToken(data.token);
      cacheCustomer(data.customer);
    }
    return data;
  } catch {
    // Silent path — never surface errors to the user; they still see order success.
    return null;
  }
};

export const logoutCustomer = () => {
  setCustomerToken("");
  cacheCustomer(null);
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const startGoogleLogin = () => {
  const redirect = `${window.location.origin}/`;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
};
